import { DirectionalHint, ICalloutContentStyles } from '@fluentui/react';
import { IPCallout } from 'baseComponents';
import { globalCalloutShadow } from 'globalStyles';
import React, { Fragment, ReactNode, useEffect, useMemo, useRef, useState } from 'react';

type CalloutLeaveBehavior = 'close' | 'delay';

interface IHoverCalloutProps {
  children: ReactNode;
  content?: ReactNode;
  calloutProps?: Omit<React.ComponentProps<typeof IPCallout>, 'target' | 'children' | 'onDismiss'>;
  openDelayMs?: number; // default 0
  closeDelayMs?: number; // default 500
  calloutLeaveBehavior?: CalloutLeaveBehavior; // default 'close'
  openOnHover?: boolean; // default true
  openOnClick?: boolean; // default true
  disabled?: boolean;
  isOpen?: boolean;
  defaultOpen?: boolean;
  wrapperClassName?: string /** Optional wrapper class/style if you need layout control */;
  wrapperStyle?: React.CSSProperties;
  isBeakVisible?: boolean; // default false
  gapSpace?: number; // default 10
  directionalHint?: DirectionalHint; // default DirectionalHint.topCenter
  onOpenChange?: (open: boolean) => void;
  renderContent?: () => ReactNode;
}

const calloutStyle: Partial<ICalloutContentStyles> = {
  beakCurtain: {
    borderRadius: 6,
  },
  root: {
    boxShadow: globalCalloutShadow,
  },
};

export const HoverCallout = ({
  children,
  content,
  renderContent,
  calloutProps,
  openDelayMs = 16, // small delay to allow component to mount and measure target before showing callout
  closeDelayMs = 500,
  calloutLeaveBehavior = 'close',
  openOnHover = true,
  openOnClick = true,
  disabled = false,
  isOpen,
  defaultOpen = false,
  onOpenChange,
  wrapperClassName,
  wrapperStyle,
  isBeakVisible = false,
  gapSpace = 10,
  directionalHint = DirectionalHint.topCenter,
}: IHoverCalloutProps) => {
  // Controlled/uncontrolled
  const controlled = typeof isOpen === 'boolean';
  const [uncontrolledOpen, setUncontrolledOpen] = useState<boolean>(defaultOpen);
  const open = controlled ? (isOpen as boolean) : uncontrolledOpen;

  const setOpen = (next: boolean) => {
    onOpenChange?.(next);
    if (!controlled) setUncontrolledOpen(next);
  };

  // DOM target (no querySelector, no ids)
  const targetRef = useRef<HTMLSpanElement | null>(null);

  // Refs for timing logic (avoid stale closure)
  const inCalloutRef = useRef<boolean>(false);
  const openTimerRef = useRef<number | null>(null);
  const closeTimerRef = useRef<number | null>(null);

  const clearOpenTimer = () => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  };

  const clearCloseTimer = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const clearTimers = () => {
    clearOpenTimer();
    clearCloseTimer();
  };

  useEffect(() => {
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scheduleOpen = () => {
    if (disabled) return;

    clearOpenTimer();

    if (openDelayMs <= 0) {
      setOpen(true);

      return;
    }

    openTimerRef.current = window.setTimeout(() => setOpen(true), openDelayMs);
  };

  const scheduleCloseUnlessInCallout = () => {
    if (disabled) return;

    clearCloseTimer();

    closeTimerRef.current = window.setTimeout(() => {
      if (!inCalloutRef.current) setOpen(false);
    }, closeDelayMs);
  };

  const cancelScheduledClose = () => {
    clearCloseTimer();
  };

  // Caller styles layer over the shadow and radius defaults instead of replacing them.
  const calloutStyles =
    calloutProps?.styles && typeof calloutProps.styles === 'object'
      ? { ...calloutStyle, ...calloutProps.styles }
      : calloutProps?.styles ?? calloutStyle;

  const resolvedContent = useMemo(() => {
    if (renderContent) return renderContent();

    return content ?? null;
  }, [renderContent, content]);

  //
  // Main render
  //
  if (disabled) {
    return <>{children}</>;
  }

  return (
    <Fragment>
      <span
        ref={targetRef}
        className={wrapperClassName}
        style={{
          display: 'inline-block',
          ...wrapperStyle,
        }}
        onMouseEnter={
          openOnHover
            ? () => {
                cancelScheduledClose();
                scheduleOpen();
              }
            : undefined
        }
        onMouseLeave={
          openOnHover
            ? () => {
                // Prevent delayed open firing after leaving
                clearOpenTimer();

                // Grace period to move into callout
                scheduleCloseUnlessInCallout();
              }
            : undefined
        }
        onClick={
          openOnClick
            ? () => {
                cancelScheduledClose();
                clearOpenTimer();
                setOpen(true);
              }
            : undefined
        }
      >
        {children}
      </span>

      {open && targetRef.current && (
        <IPCallout
          target={targetRef.current}
          isBeakVisible={isBeakVisible}
          directionalHint={directionalHint}
          gapSpace={gapSpace}
          {...calloutProps}
          styles={calloutStyles}
          onDismiss={() => {
            clearTimers();
            setOpen(false);
          }}
          onMouseEnter={(e) => {
            calloutProps?.onMouseEnter?.(e);
            inCalloutRef.current = true;
            cancelScheduledClose();
          }}
          onMouseLeave={(e) => {
            calloutProps?.onMouseLeave?.(e);
            inCalloutRef.current = false;

            if (calloutLeaveBehavior === 'delay') {
              scheduleCloseUnlessInCallout();

              return;
            }

            clearTimers();
            setOpen(false);
          }}
        >
          {resolvedContent}
        </IPCallout>
      )}
    </Fragment>
  );
};
